package diana.soleil.design.iraniancanadienbazzar.feature.home

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.lifecycle.ViewModel
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import androidx.lifecycle.viewModelScope
import diana.soleil.design.iraniancanadienbazzar.core.common.DataResult
import diana.soleil.design.iraniancanadienbazzar.core.data.CityController
import diana.soleil.design.iraniancanadienbazzar.core.data.NewsRepository
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppIconButton
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.EmptyState
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.ErrorState
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.LoadingState
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.NewsCard
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.theme.AppTheme
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.theme.appContentWidth
import diana.soleil.design.iraniancanadienbazzar.core.model.NewsArticle
import diana.soleil.design.iraniancanadienbazzar.feature.home.resources.Res
import diana.soleil.design.iraniancanadienbazzar.feature.home.resources.home_error
import diana.soleil.design.iraniancanadienbazzar.feature.home.resources.home_retry
import diana.soleil.design.iraniancanadienbazzar.feature.home.resources.home_section_news
import diana.soleil.design.iraniancanadienbazzar.feature.home.resources.news_back_a11y
import diana.soleil.design.iraniancanadienbazzar.feature.home.resources.news_list_empty
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.SharingStarted
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.flatMapLatest
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.stateIn
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel

data class NewsListUiState(
    val loading: Boolean = true,
    val articles: List<NewsArticle> = emptyList(),
    val error: Boolean = false,
)

/**
 * Every visible community story for the active metro (QA 2026-09-12 #6: news gets its own screen,
 * opened from "See all" on the Home rail). Same metro rule as the rail: national stories always show.
 */
@OptIn(ExperimentalCoroutinesApi::class)
class NewsListViewModel(
    private val newsRepository: NewsRepository,
    cityController: CityController,
) : ViewModel() {
    private val retryTrigger = MutableStateFlow(0)

    val state: StateFlow<NewsListUiState> =
        combine(cityController.activeMetroId, retryTrigger) { metroId, _ -> metroId }
            .flatMapLatest { metroId ->
                newsRepository.observeNews(metroId, limit = NEWS_LIST_LIMIT).map { result ->
                    when (result) {
                        is DataResult.Success -> NewsListUiState(loading = false, articles = result.data)
                        is DataResult.Failure -> NewsListUiState(loading = false, error = true)
                    }
                }
            }
            .catch { emit(NewsListUiState(loading = false, error = true)) }
            .stateIn(viewModelScope, SharingStarted.WhileSubscribed(5_000), NewsListUiState())

    fun retry() {
        retryTrigger.value += 1
    }

    private companion object {
        const val NEWS_LIST_LIMIT = 100
    }
}

@Composable
fun NewsListRoute(
    onBack: () -> Unit,
    onOpenArticle: (articleId: String) -> Unit,
    modifier: Modifier = Modifier,
    viewModel: NewsListViewModel = koinViewModel(),
) {
    val state by viewModel.state.collectAsStateWithLifecycle()
    val lang = AppTheme.language

    Column(
        modifier = modifier
            .background(AppTheme.colors.background)
            .appContentWidth(AppTheme.sizes.readingMaxWidth)
            .fillMaxHeight(),
    ) {
        Row(
            modifier = Modifier.fillMaxWidth().statusBarsPadding().padding(AppTheme.spacing.lg),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.md),
        ) {
            AppIconButton(
                iconName = "arrow_back",
                contentDescription = stringResource(Res.string.news_back_a11y),
                onClick = onBack,
                tint = AppTheme.colors.onSurface,
            )
            Text(
                text = stringResource(Res.string.home_section_news),
                style = AppTheme.typography.headlineLg,
                color = AppTheme.colors.onSurface,
                modifier = Modifier.weight(1f),
            )
        }
        when {
            state.loading -> LoadingState()
            state.error -> ErrorState(
                message = stringResource(Res.string.home_error),
                onRetry = viewModel::retry,
                retryLabel = stringResource(Res.string.home_retry),
            )
            state.articles.isEmpty() -> EmptyState(
                title = stringResource(Res.string.home_section_news),
                description = stringResource(Res.string.news_list_empty),
            )
            else -> LazyColumn(
                modifier = Modifier.fillMaxSize().navigationBarsPadding(),
                contentPadding = PaddingValues(
                    start = AppTheme.spacing.lg,
                    end = AppTheme.spacing.lg,
                    bottom = AppTheme.spacing.xl,
                ),
                verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.lg),
            ) {
                items(state.articles, key = { it.id }) { article ->
                    NewsCard(
                        title = article.title.resolve(lang),
                        teaser = article.summary.resolve(lang).ifBlank { article.body.resolve(lang) },
                        imageUrl = article.imageUrl,
                        onClick = { onOpenArticle(article.id) },
                        // The outer fillMaxWidth wins over the card's rail width, so it spans the list.
                        modifier = Modifier.fillMaxWidth(),
                    )
                }
            }
        }
    }
}
